'use client';

import { IconCheckmarkCircleFilled } from '@zillow/constellation-icons/react/filled/icon-checkmark-circle-filled';
import { IconCheckmarkFilled } from '@zillow/constellation-icons/react/filled/icon-checkmark-filled';
import { IconErrorFilled } from '@zillow/constellation-icons/react/filled/icon-error-filled';
import { IconHourglassFilled } from '@zillow/constellation-icons/react/filled/icon-hourglass-filled';
import { IconInfoFilled } from '@zillow/constellation-icons/react/filled/icon-info-filled';
import { IconMinusCircleFilled } from '@zillow/constellation-icons/react/filled/icon-minus-circle-filled';
import { IconMinusFilled } from '@zillow/constellation-icons/react/filled/icon-minus-filled';
import { IconWarningFilled } from '@zillow/constellation-icons/react/filled/icon-warning-filled';
import { forwardRef } from 'react';

import type { StatusType } from '../../types/statuses';
import { Icon } from '../icon/icon';
import type { ToneIconPropsInterface } from './types/tone-icon.types';

const toneTitles: Record<StatusType, string> = {
  complete: 'Complete',
  critical: 'Error',
  incomplete: 'Incomplete',
  info: 'Information',
  success: 'Success',
  transient: 'Pending',
  warning: 'Warning',
};

/**
 * Provides a semantic mapping between `tone` values and associated icons.
 *
 * **For internal use only!**
 *
 * @since 10.0.0
 */
export const ToneIcon = forwardRef<SVGSVGElement, Omit<ToneIconPropsInterface, 'children'>>(
  (props: ToneIconPropsInterface, forwardedRef) => {
    const { tone = 'info', includeShape = true, ...rest } = props;
    let iconContent;

    switch (tone) {
      case 'complete':
      case 'success': {
        iconContent = includeShape ? <IconCheckmarkCircleFilled /> : <IconCheckmarkFilled />;
        break;
      }
      case 'warning': {
        iconContent = <IconWarningFilled />;
        break;
      }
      case 'critical': {
        iconContent = <IconErrorFilled />;
        break;
      }
      case 'incomplete': {
        iconContent = includeShape ? <IconMinusCircleFilled /> : <IconMinusFilled />;
        break;
      }
      case 'info': {
        iconContent = <IconInfoFilled />;
        break;
      }
      case 'transient': {
        iconContent = <IconHourglassFilled />;
        break;
      }
      default: {
        return;
      }
    }

    return (
      <Icon
        title={toneTitles[tone]}
        data-c11n-component="ToneIcon"
        data-c11n-version={__C11N_VERSION__}
        ref={forwardedRef}
        {...rest}
      >
        {iconContent}
      </Icon>
    );
  },
);

ToneIcon.displayName = 'ToneIcon';
